import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin, List, Avatar, Space, Tag } from 'antd';
import { SearchOutlined, UserOutlined, MailOutlined, PhoneOutlined, PlusOutlined } from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { debugInfo, debugLog } from '../../utils/debug';
import ImageShow from '../../Uitiles/ImageShow';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';

const { Option } = Select;

const AddHospitalDoctor = ({ visible, onCancel, onSuccess, hospitalId }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [availableDoctors, setAvailableDoctors] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [addNewDoctor, setAddNewDoctor] = useState(false);
    const [showCreateDoctor, setShowCreateDoctor] = useState(false);
    const [createDoctorLoading, setCreateDoctorLoading] = useState(false);
    const [doctorPic, setDoctorPic] = useState(null);
    const [specialities, setSpecialities] = useState([]);
    const [hospitalDefaultLocation, setHospitalDefaultLocation] = useState(null);
    const [doctorPicUrl, setDoctorPicUrl] = useState(null);
    const [doctorPicId, setDoctorPicId] = useState(null);
    const [selecterdoctorid, setSelecterdoctorid] = useState(null);

    useEffect(() => {
        if (visible && searchText) {
            searchDoctors();
            setShowCreateDoctor(false);
        }
    }, [searchText, visible]);

    useEffect(() => {
        getSpecialities();
        getHospitalDefaultLocation();
    }, []);


    const getHospitalDefaultLocation = async () => {
        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'hospitals_default_location';
            const conditions = {
                hospital_id: hospitalId
            };
            const response = await oneKlicks.read(tableSchema, tableName, conditions, '', '', '', []);
            if (response && response.data && response.data.length > 0) {
                debugInfo(response.data);
                setHospitalDefaultLocation(response.data[0]);
            } else {
                debugInfo('No hospital default location found');
                setHospitalDefaultLocation(null);
            }
        } catch (error) {
            console.error('Error getting hospital default location:', error);
            message.error('Failed to get hospital default location');
        } finally {
            setLoading(false);
        }

    }

    const getSpecialities = async () => {
        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'specialities';
            const conditions = {};
            const orderBy = 'name ASC';
            const search = {};
            const pagination = '';
            const margedata = [];
            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            if (response && response.data && response.data.length > 0) {
                debugInfo(response.data);
                setSpecialities(response.data);
            } else {
                debugInfo('No specialities found');
                setSpecialities([]);
            }
        } catch (error) {
            console.error('Error getting specialities:', error);
            message.error('Failed to get specialities');
        } finally {
            setLoading(false);
        }
    }

    const searchDoctors = async () => {
        if (!searchText.trim()) {
            setAvailableDoctors([]);
            return;
        }

        try {
            setSearching(true);
            console.log('Searching for doctors with text:', searchText);

            const tableSchema = '';
            const tableName = 'users';
            const conditions = {
                user_type: 'doctor',
                status: 1 // Only active doctors
            };
            const orderBy = 'display_name ASC';
            const search = {
                display_name: searchText,
                email: searchText,
                phone: searchText
            };
            const pagination = '';
            const margedata = [];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            console.log('Search response:', response);

            if (response && response.data) {
                console.log('Found doctors:', response.data.length);
                setAvailableDoctors(response.data);
            } else {
                console.log('No doctors found');
                setAvailableDoctors([]);
            }
        } catch (error) {
            console.error('Error searching doctors:', error);
            message.error('Failed to search doctors');
            setAvailableDoctors([]);
        } finally {
            setSearching(false);
        }
    };

    const handleDoctorSelect = (doctor) => {
        debugInfo("selected doctor :", doctor);
        setSelecterdoctorid(doctor.id);
        setSelectedDoctor(doctor);
        form.setFieldsValue({
            doctor_id: doctor.id,
            doctor_name: doctor.display_name,
            doctor_email: doctor.email,
            doctor_phone: doctor.phone,
            doctor_speciality: doctor.speciality_id,
            status: 'active'
        });
    };

    const handleSubmit = async (values) => {
        if (!selectedDoctor) {
            message.error('Please select a doctor first');
            return;
        }

        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'hospital_doctors';
            const data = {
                hospital_id: hospitalId,
                doctor_id: selectedDoctor.id,
                status: 1, // 1 = active (changed from 0 to add directly as active)
                created_at: formatDateForMySQL(new Date()),
                updated_at: formatDateForMySQL(new Date())
            };
            const conditions = {
                doctor_id: selectedDoctor.id,
                hospital_id: hospitalId
            };

            const response = await oneKlicks.create(tableSchema, tableName, data, conditions);

            debugInfo("response :", response);

            if (response && response.success) {
                message.success(`Dr. ${selectedDoctor.display_name} added to hospital successfully!`);
                form.resetFields();
                setSelectedDoctor(null);
                setSearchText('');
                setAvailableDoctors([]);
                onSuccess();
            } else if (response && response.message && response.message.includes('already exists')) {
                // Doctor already associated with this hospital
                message.warning(`Dr. ${selectedDoctor.display_name} is already associated with this hospital.`);
                console.log('Existing association:', response.existingData);

                // Still close the modal and refresh
                form.resetFields();
                setSelectedDoctor(null);
                setSearchText('');
                setAvailableDoctors([]);
                onSuccess();
            } else {
                message.error('Failed to add doctor to hospital. Please try again.');
            }
        } catch (error) {
            console.error('Error adding doctor to hospital:', error);
            message.error('An error occurred while adding the doctor.');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format date for MySQL
    const formatDateForMySQL = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedDoctor(null);
        setSearchText('');
        setAvailableDoctors([]);
        onCancel();
        setSelecterdoctorid(null);
    };

    const handleCreateDoctor = async (values) => {
        try {
            setCreateDoctorLoading(true);
            console.log('Creating doctor with values:', values);

            // 1. Create doctor in users table
            const tableSchema = '';
            const tableName = 'users';
            // Helper function to format date for MySQL
            const formatDateForMySQL = (date) => {
                const d = new Date(date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const seconds = String(d.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            };

            const doctorData = {
                user_type: 'doctor',
                first_name: values.first_name,
                last_name: values.last_name,
                display_name: values.first_name + ' ' + values.last_name,
                email: values.email,
                phone: values.phone,
                speciality_id: values.speciality_id,
                profile_photo: doctorPicId || '',
                profile_photo_url: doctorPicUrl || '',
                status: 1, // 1 = active, 0 = inactive, 2 = deleted
                created_at: formatDateForMySQL(new Date()),
                updated_at: formatDateForMySQL(new Date())
            };

            console.log('Doctor data to create:', doctorData);
            const response = await oneKlicks.create(tableSchema, tableName, doctorData);
            console.log('Create doctor response:', response);

            if (response && response.success) {
                // Get the doctor ID from response
                const doctorId = response.insertedData?.id || response.data?.id;
                console.log('Created doctor ID:', doctorId);

                if (!doctorId) {
                    console.error('No doctor ID in response:', response);
                    message.error('Failed to get doctor ID after creation');
                    return;
                }

                // 2. Associate doctor with hospital
                const hospitalDoctorData = {
                    hospital_id: hospitalId,
                    doctor_id: doctorId,
                    status: 1, // 1 = active
                    created_at: formatDateForMySQL(new Date()),
                    updated_at: formatDateForMySQL(new Date())
                };
                const conditions = {
                    doctor_id: doctorId,
                    hospital_id: hospitalId
                };

                console.log('Associating doctor with hospital:', hospitalDoctorData);
                const responsed = await oneKlicks.create(tableSchema, 'hospital_doctors', hospitalDoctorData, conditions);
                console.log('Hospital-doctor association response:', responsed);

                if (responsed && responsed.success) {
                    // 3. Create doctor default location if hospital has one
                    if (hospitalDefaultLocation && hospitalDefaultLocation.address) {
                        const locationData = {
                            user_id: doctorId,
                            address: hospitalDefaultLocation.address,
                            lat: hospitalDefaultLocation.lat,
                            lng: hospitalDefaultLocation.lng,
                            status: 1, // 1 = active
                            created_at: formatDateForMySQL(new Date()),
                            updated_at: formatDateForMySQL(new Date())
                        };
                        const locationConditions = {
                            user_id: doctorId
                        };

                        console.log('Creating doctor location:', locationData);
                        const locationResponse = await oneKlicks.create(tableSchema, 'doctor_default_location', locationData, locationConditions);
                        console.log('Location creation response:', locationResponse);
                    }

                    message.success('Doctor created and added to hospital successfully');
                    setShowCreateDoctor(false);
                    setDoctorPic(null);
                    setDoctorPicId(null);
                    setDoctorPicUrl(null);
                    onSuccess();
                } else if (responsed && responsed.message && responsed.message.includes('already exists')) {
                    // Doctor was created but association already exists
                    console.log('Doctor created but already associated with hospital');
                    message.warning('Doctor created successfully but is already associated with this hospital');
                    setShowCreateDoctor(false);
                    setDoctorPic(null);
                    setDoctorPicId(null);
                    setDoctorPicUrl(null);
                    onSuccess();
                } else {
                    console.error('Failed to associate doctor with hospital:', responsed);
                    message.error('Doctor created but failed to associate with hospital. Please try adding from existing doctors.');
                    setShowCreateDoctor(false);
                    setDoctorPic(null);
                    setDoctorPicId(null);
                    setDoctorPicUrl(null);
                    onSuccess();
                }
            } else {
                console.error('Failed to create doctor:', response);
                message.error('Failed to create doctor');
            }
        } catch (error) {
            console.error('Error creating doctor:', error);
            message.error('Failed to create doctor: ' + error.message);
        } finally {
            setCreateDoctorLoading(false);
        }
    };

    const handleUploadImage = async (file) => {
        try {

            const root = "doctors/profile";
            const status = "public";
            const response = await OneKlicksFileSDK.uploadFile(root, status, file);
            if (response && response.success && response.id) {
                // Get the file URL using the file ID
                const fileUrlResponse = await OneKlicksFileSDK.getfile(response.id);
                if (fileUrlResponse && fileUrlResponse.success) {
                    setDoctorPicId(response.id);
                    setDoctorPicUrl(fileUrlResponse.fileUrl);
                } else {
                    message.error('Failed to get file URL');
                }
            } else {
                message.error('Failed to upload image');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            message.error('Failed to upload image');
        }
    }

    const handleAddNewDoctor = () => {
        setShowCreateDoctor(true);
    };

    return (
        <Modal
            title="Add Doctor to Hospital"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={800}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <div style={{ marginBottom: 20 }}>
                    <h4>Search and Select Doctor</h4>
                    <br />
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Input
                            placeholder="Search doctors by name or email..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ marginBottom: 10 }}
                        />
                        <Button type="primary" style={{ height: '60px' }} className='dc-btn dc-btnactive' icon={<SearchOutlined />} onClick={handleAddNewDoctor}>Add New Doctor</Button>

                    </div>

                    {searching && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spin size="small" />
                            <p>Searching doctors...</p>
                        </div>
                    )}

                    {!searching && availableDoctors.length > 0 && !selecterdoctorid && !showCreateDoctor && (
                        <List
                            itemLayout="horizontal"
                            dataSource={availableDoctors}
                            renderItem={(doctor) => (
                                <List.Item
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedDoctor?.id === doctor.id ? '#f0f0f0' : 'white',
                                        padding: '10px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        marginBottom: '8px'
                                    }}
                                    onClick={() => handleDoctorSelect(doctor)}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                size={40}
                                                src={doctor.profile_photo ? <ImageShow image={doctor.profile_photo} width={40} height={40} /> : null}
                                                icon={<UserOutlined />}
                                            />
                                        }
                                        title={
                                            <Space>
                                                <strong>{doctor.display_name}</strong>
                                                {selectedDoctor?.id === doctor.id && (
                                                    <Tag color="green" icon={<PlusOutlined />}>
                                                        Selected
                                                    </Tag>
                                                )}
                                            </Space>
                                        }
                                        description={
                                            <div>
                                                <div><MailOutlined /> {doctor.email}</div>
                                                <div><PhoneOutlined /> {doctor.phone}</div>
                                                {doctor.speciality_id && (
                                                    <div>Speciality: {doctor.speciality_id}</div>
                                                )}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    )}

                    {!searching && searchText && availableDoctors.length === 0 && !showCreateDoctor && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            <div>No doctors found matching your search.</div>
                            <br />
                            <a className='dc-btn dc-btnactive' href='#' onClick={() => setShowCreateDoctor(true)}>Add New Doctor</a>

                        </div>
                    )}

                    {showCreateDoctor && (
                        <Form
                            layout="vertical"
                            onFinish={handleCreateDoctor}
                            style={{ marginTop: 24, background: '#fafafa', padding: 24, borderRadius: 8 }}
                        >
                            <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}> <Input /> </Form.Item>
                            <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}> <Input /> </Form.Item>
                            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}> <Input /> </Form.Item>
                            <Form.Item name="phone" label="Phone Number" rules={[{ required: true }]}> <Input /> </Form.Item>
                            <Form.Item name="speciality_id" label="Specialization" rules={[{ required: true }]} initialValue={specialities[0].id} style={{ height: "50px", width: '100%' }}> <Select placeholder='Select Specialization' showSearch optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0} style={{ height: "45px", width: '100%' }}>
                                {specialities.map((speciality) => (
                                    <Option key={speciality.id} value={speciality.id}>{speciality.name}</Option>
                                ))}
                            </Select> </Form.Item>
                            <br />
                            <Form.Item name="profile_photo" label="Profile Picture">
                                <Input type="file" accept="image/*" onChange={e => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = (ev) => setDoctorPic(ev.target.result);
                                        reader.readAsDataURL(file);
                                        handleUploadImage(file);
                                    }
                                }} style={{ height: "45px", width: '100%' }} />
                                {doctorPic && <img src={doctorPic} alt="Doctor" style={{ width: 60, height: 60, borderRadius: '50%', marginTop: 8 }} />}
                            </Form.Item>
                            <Form.Item>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <a className='dc-btn' href='#' onClick={() => setShowCreateDoctor(false)}>Cancel</a>
                                    <a className='dc-btn dc-btnactive' href='#' onClick={() => handleCreateDoctor(form.getFieldsValue())}>Create Doctor</a>
                                </div>
                            </Form.Item>
                        </Form>
                    )}
                </div>

                {selecterdoctorid && (
                    <>
                        <Form.Item
                            name="doctor_id"
                            label="Doctor ID"
                            hidden
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="doctor_name"
                            label="Doctor Name"
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            name="doctor_email"
                            label="Email"
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            name="doctor_phone"
                            label="Phone"
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            name="doctor_speciality"
                            label="Speciality"
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            name="status"
                            label="Status"
                            initialValue="active"
                        >
                            <Select style={{ height: "45px", width: '100%' }}>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <a className='dc-btn' href='#' onClick={handleCancel}>Cancel</a>
                                <a className='dc-btn dc-btnactive' href='#' onClick={() => handleSubmit(form.getFieldsValue())}>Add Doctor to Hospital</a>
                            </div>
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default AddHospitalDoctor; 
